"use client";

import type { BlogPostCard, EnrichedProfile } from "@/lib/supabase/types";
import { cn, copyToClipboard } from "@/lib/utils";
import { CheckIcon, CopyIcon, MailIcon, QrCodeIcon, Share } from "lucide-react";
import Link from "next/link";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { FaFacebook, FaLinkedin } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { toast } from "sonner";
import { ProfileInfo } from "../account/profile/profile-hover-card";
import { PostPreviewCard } from "../blog/posts-carousel";
import Modal from "./Modal";
import { Button } from "./button";
import CardQRCode from "./qr-code-card";
import { TooltipContent } from "./tooltip-content";

type Props = {
	title: string;
	url: string;
	type?: "post" | "profile";
	text?: string;
	qrCode?: boolean;
	slug?: string;
	profile?: EnrichedProfile;
	post?: BlogPostCard;
	className?: string;
};

const isWebShareSupported = (data: ShareData) => {
	// we prefer to check if the api is really available
	return (
		typeof window.navigator.canShare === "function" &&
		typeof window.navigator.share === "function" &&
		window.navigator.canShare(data)
	);
};

type ShareIconProps = {
	url: string;
	title: string;
	children?: ReactNode;
};

const ShareIcon = ({ title, url, children }: ShareIconProps) => (
	<li>
		<Link
			title={title}
			href={url}
			className="block rounded-2xl bg-primary/10 hover:bg-primary/20 p-2 hover:text-primary"
		>
			{children}
		</Link>
	</li>
);

const CopyButton = ({ url }: { url: string }) => {
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (copied) {
			const id = setTimeout(() => setCopied(false), 2000);
			return () => clearTimeout(id);
		}
	}, [copied]);

	const copy = () => {
		setCopied(true);
		copyToClipboard(url);
		toast("Successfully copied", {
			icon: <CopyIcon className="w-4 h-4" />,
			style: {
				background: "green",
				color: "white",
			},
		});
	};

	return (
		<li>
			<button
				type="button"
				title="Copy url to clipboard"
				onClick={copy}
				className="rounded-2xl bg-primary/10 p-2 hover:bg-primary/20"
			>
				{copied ? <CheckIcon size={32} /> : <CopyIcon size={32} />}
			</button>
		</li>
	);
};

export default function ShareButton(props: Props) {
	const [isOpen, setIsOpen] = useState(false);

	const completeUrl = `${props.url}?utm_campaign=social-sharing&utm_source=btn&utm_medium=`;

	const onClick = useCallback(async () => {
		const data: ShareData = {
			title: props.title,
			text: props.text,
			url: `${completeUrl}native`,
		};
		setIsOpen(true);
		if (isWebShareSupported(data)) {
			await window.navigator.share(data);
		}
		// setIsOpen(true);
	}, [props.title, props.text, completeUrl]);

	const shareButton = useMemo(
		() => (
			// <TooltipContent content="Share">
			<Button
				type="button"
				variant="ghost"
				onClick={onClick}
				className={cn(
					"z-30 group flex items-center text-primary gap-1 px-1 h-6 rounded-xl",
					// "px-2 py-1 rounded-md border border-primary/20 bg-primary/20 hover:border-primary hover:bg-primary/20",
					props.className,
				)}
			>
				{props.text ? props.text : ""}
				<Share className="size-4 group-hover:stroke-[3px]" />
			</Button>
			// </TooltipContent>
		),
		[props.className, onClick, props.text],
	);

	const isProfile = props.type === "profile";
	const isPost = props.type === "post";

	return (
		<Modal
			opened={isOpen}
			// title={`Share ${props.type}`}
			// description={props.title}
			trigger={shareButton}
			className="p-6"
			onClose={() => setIsOpen(!isOpen)}
		>
			{isPost && props.post && (
				// <Image
				// 	src={`${getUrl()}/og?slug=${props.slug}`}
				// 	alt={props.title}
				// 	width={600}
				// 	height={600}
				// 	className="rounded-2xl"
				// />
				<PostPreviewCard post={props.post} />
			)}

			{isProfile && <ProfileInfo profile={props.profile as EnrichedProfile} />}

			<ShareLinkList
				title={props.title}
				text={props.text}
				url={completeUrl}
				type={props.type}
			/>
		</Modal>
	);
}

export const ShareLinkList = ({ title, text, url, type }: Props) => {
	const [showQRCode, setShowQRCode] = useState(false);
	return (
		<ul className="z-[50] mt-3 flex w-full justify-around gap-2 text-base-500">
			<TooltipContent content="Share on Twitter">
				<ShareIcon
					title="Share on Twitter"
					url={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
						`${url}twitter`,
					)}&text=${encodeURIComponent(title)}`}
				>
					<FaXTwitter size={32} />
				</ShareIcon>
			</TooltipContent>

			<TooltipContent content="Share on Facebook">
				<ShareIcon
					title="Share on Facebook"
					url={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
						`${url}facebook`,
					)}`}
				>
					<FaFacebook size={32} />
				</ShareIcon>
			</TooltipContent>

			<TooltipContent content="Share on LinkedIn">
				<ShareIcon
					title="Share on LinkedIn"
					url={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
						`${url}linkedin`,
					)}`}
				>
					<FaLinkedin size={32} />
				</ShareIcon>
			</TooltipContent>

			<TooltipContent content="Send by e-mail">
				<ShareIcon
					title="Send by e-mail"
					url={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
						`${text}\n\n`,
					)}${encodeURIComponent(`${url}email`)}`}
				>
					<MailIcon size={32} />
				</ShareIcon>
			</TooltipContent>

			<TooltipContent content="Copy link">
				<button
					type="button"
					title="Show QR Code"
					// variant="ghost"
					className="rounded-2xl bg-primary/10 p-2 hover:bg-primary/20"
					onClick={() => setShowQRCode(!showQRCode)}
				>
					<QrCodeIcon size={32} />
				</button>
			</TooltipContent>

			{showQRCode && (
				<QRCode
					url={`${url}copy`}
					type={type}
					onClose={() => setShowQRCode(false)}
				/>
			)}

			<TooltipContent content="Copy link">
				<CopyButton url={`${url}copy`} />
			</TooltipContent>
		</ul>
	);
};

interface QRCodeProps {
	url: string;
	type?: "post" | "profile";
	onClose?: () => void;
}

function QRCode({ url, type, onClose }: QRCodeProps) {
	return (
		<Modal
			opened={true}
			onClose={onClose}
			title="QR Code"
			className="p-10"
			description={`Scan the QR code to share ${type}`}
		>
			<CardQRCode url={url} className="" />
		</Modal>
	);
}
